import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiClient } from '../../services/api';

// ── Fix Leaflet default marker icons (webpack strips the paths) ────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ── Custom icons ───────────────────────────────────────────────────
const arcgisIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'arcgis-marker',
});

const blipIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'blip-marker',
});

// ── Types ──────────────────────────────────────────────────────────
interface GeoFeature {
  lat: number;
  lng: number;
  label: string;
  zoneCode?: string;
  restrictionType?: string;
  metadata?: Record<string, unknown>;
}

interface GeoResponse {
  arcgis: GeoFeature[] | { error: string };
  blip: GeoFeature[] | { error: string };
}

interface StateJurisdictionResponse {
  state: string;
  jurisdictionCount: number;
  arcgis: GeoFeature[] | { error: string };
  blip: GeoFeature[] | { error: string };
}

// ── Helper: auto-fit map to markers ────────────────────────────────
function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map(([lat, lng]) => [lat, lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [points, map]);
  return null;
}

// ── Component ──────────────────────────────────────────────────────
const ZoningMap: React.FC = () => {
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [arcgisFeatures, setArcgisFeatures] = useState<GeoFeature[]>([]);
  const [blipFeatures, setBlipFeatures] = useState<GeoFeature[]>([]);
  const [arcgisError, setArcgisError] = useState<string | null>(null);
  const [blipError, setBlipError] = useState<string | null>(null);
  const [showArcgis, setShowArcgis] = useState(true);
  const [showBlip, setShowBlip] = useState(true);
  const [scanLimit, setScanLimit] = useState(25);
  const [lastScanSummary, setLastScanSummary] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!state) return;
    setLoading(true);
    setError(null);
    setArcgisError(null);
    setBlipError(null);
    setLastScanSummary(null);
    setArcgisFeatures([]);
    setBlipFeatures([]);

    try {
      const res = await apiClient.post<GeoResponse>('/admin/zoning/geo', {
        state,
        city: city || undefined,
      });

      const data = res.data;

      if (Array.isArray(data.arcgis)) {
        setArcgisFeatures(data.arcgis);
      } else {
        setArcgisError(data.arcgis.error);
      }

      if (Array.isArray(data.blip)) {
        setBlipFeatures(data.blip);
      } else {
        setBlipError(data.blip.error);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Geo lookup failed';
      setError(msg);
    }
    setLoading(false);
  };

  const handleStateJurisdictionScan = async () => {
    if (!state) return;
    setLoading(true);
    setError(null);
    setArcgisError(null);
    setBlipError(null);
    setLastScanSummary(null);
    setArcgisFeatures([]);
    setBlipFeatures([]);

    try {
      const res = await apiClient.post<StateJurisdictionResponse>('/admin/zoning/geo/state-jurisdictions', {
        state,
        limit: scanLimit,
      });

      const data = res.data;

      if (Array.isArray(data.arcgis)) {
        setArcgisFeatures(data.arcgis);
      } else {
        setArcgisError(data.arcgis.error);
      }

      if (Array.isArray(data.blip)) {
        setBlipFeatures(data.blip);
      } else {
        setBlipError(data.blip.error);
      }

      setLastScanSummary(
        `Loaded ${Array.isArray(data.arcgis) ? data.arcgis.length : 0} mapped jurisdiction point(s) from ${data.jurisdictionCount} saved zoning jurisdiction(s) in ${data.state}.`,
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'State jurisdiction scan failed';
      setError(msg);
    }

    setLoading(false);
  };

  // All valid points for bounds fitting
  const allPoints: [number, number][] = [
    ...(showArcgis ? arcgisFeatures : []),
    ...(showBlip ? blipFeatures : []),
  ]
    .filter((f) => f.lat && f.lng)
    .map((f) => [f.lat, f.lng]);

  const hasResults = arcgisFeatures.length > 0 || blipFeatures.length > 0;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
      {/* Search bar */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 space-y-3">
        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Zone &amp; Billboard Map</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Search a state/city to see geocoded locations (ArcGIS) and nearby billboard inventory (Blip) on the map.
        </p>
        <div className="flex gap-3 items-end">
          <div>
            <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">State</label>
            <input
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="e.g. TX"
              className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm w-24 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">City</label>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Dallas"
              className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm w-40 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading || !state}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
          <button
            onClick={handleStateJurisdictionScan}
            disabled={loading || !state}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {loading ? 'Scanning...' : 'Scan state jurisdictions'}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Jurisdiction scan limit
          </label>
          <select
            value={scanLimit}
            onChange={(e) => setScanLimit(Number(e.target.value))}
            className="rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs"
          >
            {[10, 20, 25, 30, 40].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span className="text-xs text-slate-400">Uses your saved zoning jurisdictions for the selected state.</span>
        </div>

        {/* Layer toggles */}
        {hasResults && (
          <div className="flex gap-4 pt-1">
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 cursor-pointer">
              <input type="checkbox" checked={showArcgis} onChange={() => setShowArcgis(!showArcgis)} className="rounded border-slate-300 dark:border-slate-600" />
              <span className="inline-block w-3 h-3 rounded-full bg-blue-500" />
              ArcGIS Locations ({arcgisFeatures.length})
            </label>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 cursor-pointer">
              <input type="checkbox" checked={showBlip} onChange={() => setShowBlip(!showBlip)} className="rounded border-slate-300 dark:border-slate-600" />
              <span className="inline-block w-3 h-3 rounded-full bg-orange-500" />
              Blip Billboards ({blipFeatures.length})
            </label>
          </div>
        )}

        {/* Errors / warnings */}
        {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</p>}
        {arcgisError && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            ArcGIS: {arcgisError}
          </p>
        )}
        {blipError && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Blip: {blipError}
          </p>
        )}
        {lastScanSummary && (
          <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
            {lastScanSummary}
          </p>
        )}
      </div>

      {/* Map */}
      <div style={{ height: 480 }}>
        <MapContainer
          center={[39.8283, -98.5795]}
          zoom={4}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {allPoints.length > 0 && <FitBounds points={allPoints} />}

          {/* ArcGIS markers (blue) */}
          {showArcgis &&
            arcgisFeatures
              .filter((f) => f.lat && f.lng)
              .map((f, i) => (
                <Marker key={`arcgis-${i}`} position={[f.lat, f.lng]} icon={arcgisIcon}>
                  <Popup>
                    <>
                      <p className="font-semibold text-xs">{f.label}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Source: ArcGIS</p>
                      {f.metadata?.City && <p className="text-xs">City: {String(f.metadata.City)}</p>}
                      {f.metadata?.Region && <p className="text-xs">Region: {String(f.metadata.Region)}</p>}
                    </>
                  </Popup>
                </Marker>
              ))}

          {/* Blip markers (orange) */}
          {showBlip &&
            blipFeatures
              .filter((f) => f.lat && f.lng)
              .map((f, i) => (
                <Marker key={`blip-${i}`} position={[f.lat, f.lng]} icon={blipIcon}>
                  <Popup>
                    <>
                      <p className="font-semibold text-xs">{f.label}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Source: Blip Billboards</p>
                      {f.zoneCode && <p className="text-xs">Zone: {f.zoneCode}</p>}
                      {f.restrictionType && (
                        <p className="text-xs">
                          Restriction:{' '}
                          <span
                            className={
                              f.restrictionType === 'ban'
                                ? 'text-red-600 font-medium'
                                : f.restrictionType === 'restriction'
                                ? 'text-amber-600 font-medium'
                                : ''
                            }
                          >
                            {f.restrictionType}
                          </span>
                        </p>
                      )}
                      {f.metadata?.size && <p className="text-xs">Size: {String(f.metadata.size)}</p>}
                      {f.metadata?.facing && <p className="text-xs">Facing: {String(f.metadata.facing)}</p>}
                      {f.metadata?.weeklyImpressions != null && (
                        <p className="text-xs">Weekly impressions: {Number(f.metadata.weeklyImpressions).toLocaleString()}</p>
                      )}
                    </>
                  </Popup>
                </Marker>
              ))}
        </MapContainer>
      </div>

      {/* Summary bar */}
      {hasResults && (
        <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex gap-4 text-[11px] text-slate-500 dark:text-slate-400">
          <span>ArcGIS: {arcgisFeatures.length} location(s)</span>
          <span>Blip: {blipFeatures.length} billboard(s)</span>
        </div>
      )}
    </div>
  );
};

export default ZoningMap;
